import { getWeek } from 'date-fns';

import { availableLocaleConfigs } from '~src/commons/utils/translations/localization';

import {
  MaxFormattingFunction,
  MaxFormattingFunctionParams,
  addDaysToNow,
  getDeliveryWeeks,
} from '.';

const WEEK_LENGTH = 7;

export const formatDays: MaxFormattingFunction = ({ deliveryTime, translate }) => {
  return translate(
    deliveryTime.unit === 'business_day'
      ? 'widget.delivery_time.MAX_BUSINESS_DAYS'
      : 'widget.delivery_time.MAX_DAYS',
    {
      max: deliveryTime.max,
    }
  );
};

export const formatDate: MaxFormattingFunction = ({
  deliveryTime,
  translate,
  format,
  deliveryDaysAreTheSame,
}) => {
  const deliveryDate = addDaysToNow(deliveryTime.max);

  return translate(
    deliveryDaysAreTheSame
      ? 'widget.delivery_time.MAX_DATE_EXACT'
      : 'widget.delivery_time.MAX_DATE',
    {
      dayOfMonthMax: format(deliveryDate, translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')),
      MAX_MONTH_NUMBER: translate(
        `widget.delivery_time.MAX_MONTH_NUMBER_${format(deliveryDate, 'M')}`
      ),
    }
  );
};

export const formatWeekday: MaxFormattingFunction = ({
  deliveryTime,
  translate,
  format,
  deliveryDaysAreTheSame,
}) => {
  const deliveryDate = addDaysToNow(deliveryTime.max);

  if (deliveryTime.max < WEEK_LENGTH) {
    return translate(
      deliveryDaysAreTheSame
        ? 'widget.delivery_time.MAX_WEEKDAY_EXACT'
        : 'widget.delivery_time.MAX_WEEKDAY',
      {
        MAX_WEEKDAY_NUMBER: translate(
          `widget.delivery_time.MAX_WEEKDAY_NUMBER_${format(deliveryDate, 'i')}`
        ),
      }
    );
  } else {
    return translate(
      deliveryDaysAreTheSame
        ? 'widget.delivery_time.MAX_WEEKDAY_DATE_EXACT'
        : 'widget.delivery_time.MAX_WEEKDAY_DATE',
      {
        MAX_WEEKDAY_NUMBER: translate(
          `widget.delivery_time.MAX_WEEKDAY_NUMBER_${format(deliveryDate, 'i')}`
        ),
        dayOfMonthMax: format(deliveryDate, translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')),
        MAX_MONTH_NUMBER: translate(
          `widget.delivery_time.MAX_MONTH_NUMBER_${format(deliveryDate, 'M')}`
        ),
      }
    );
  }
};

export const formatWeekdayDate: MaxFormattingFunction = ({
  deliveryTime,
  translate,
  format,
  deliveryDaysAreTheSame,
}) => {
  const deliveryDate = addDaysToNow(deliveryTime.max);

  return translate(
    deliveryDaysAreTheSame
      ? 'widget.delivery_time.MAX_WEEKDAY_DATE_EXACT'
      : 'widget.delivery_time.MAX_WEEKDAY_DATE',
    {
      MAX_WEEKDAY_NUMBER: translate(
        `widget.delivery_time.MAX_WEEKDAY_NUMBER_${format(deliveryDate, 'i')}`
      ),
      dayOfMonthMax: format(deliveryDate, translate('widget.delivery_time.DAY_OF_MONTH_FORMAT')),
      MAX_MONTH_NUMBER: translate(
        `widget.delivery_time.MAX_MONTH_NUMBER_${format(deliveryDate, 'M')}`
      ),
    }
  );
};

export const formatWeeks: MaxFormattingFunction = ({
  deliveryTime,
  translate,
  locale,
  deliveryWeeksAreTheSame,
}) => {
  const { deliveryWeekMax } = getDeliveryWeeks(deliveryTime, locale);

  return translate(
    deliveryWeeksAreTheSame
      ? 'widget.delivery_time.MAX_WEEKS_EXACT'
      : 'widget.delivery_time.MAX_WEEKS',
    {
      max: deliveryWeekMax,
    }
  );
};

type FormatWeekNumbers = (
  params: Omit<
    MaxFormattingFunctionParams,
    'format' | 'deliveryDaysAreTheSame' | 'isTomorrowHoliday'
  > & { addTranslations?: boolean }
) => string;

export const formatWeekNumbers: FormatWeekNumbers = ({
  deliveryTime,
  translate,
  locale,
  deliveryWeeksAreTheSame,
  addTranslations = true,
}) => {
  const deliveryDate = addDaysToNow(deliveryTime.max);
  const localeConfig = availableLocaleConfigs[locale];
  const maxWeek = getWeek(deliveryDate, { locale: localeConfig });

  return addTranslations
    ? translate(
        deliveryWeeksAreTheSame
          ? 'widget.delivery_time.MAX_WEEK_NUMBER_EXACT'
          : 'widget.delivery_time.MAX_WEEK_NUMBER',
        {
          max: maxWeek,
        }
      )
    : maxWeek.toString();
};
